import { useEffect, useMemo, useState } from 'react'
import { startersApiService, type Starter } from '../../services/startersApiService'
import { ApiError } from '../../services/BaseApiService'
import StarterThumbnail from './StarterThumbnail'
import StarterPreviewModal from './StarterPreviewModal'
import { fieldStyle } from '../blocks/BlockDetailsFields'
import { colors, fontSize, radius, shadow, spacing } from '../../styles/theme'

interface StarterGalleryProps {
  /** Live workspace: browse and preview only. */
  readOnly?: boolean
  initialFolderId?: string
}

function capitalize(s: string) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

export default function StarterGallery({ readOnly = false, initialFolderId }: StarterGalleryProps) {
  const [starters, setStarters] = useState<Starter[] | null>(null)
  const [error, setError] = useState('')
  const [category, setCategory] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState<Starter | null>(null)

  useEffect(() => {
    let cancelled = false
    startersApiService.list()
      .then(s => { if (!cancelled) setStarters(s) })
      .catch(err => { if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load layouts') })
    return () => { cancelled = true }
  }, [])

  const groups = useMemo(() => {
    const map = new Map<string, Starter[]>()
    for (const s of starters ?? []) {
      const list = map.get(s.category) ?? []
      list.push(s)
      map.set(s.category, list)
    }
    return [...map.entries()]
  }, [starters])

  if (error) return <div role="alert" style={{ color: colors.error, fontSize: fontSize.sm }}>{error}</div>
  if (!starters) return <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>Loading layouts…</div>
  if (starters.length === 0) return <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>No layouts available.</div>

  const chip = (label: string, value: string | null) => {
    const active = category === value
    return (
      <button
        key={label}
        type="button"
        aria-pressed={active}
        onClick={() => setCategory(value)}
        style={{
          padding: '4px 12px', borderRadius: radius.full, fontSize: fontSize.sm, cursor: 'pointer',
          border: `1px solid ${active ? colors.primary : colors.border}`,
          background: active ? colors.primaryLight : colors.surface,
          color: active ? colors.text : colors.textSecondary,
        }}
      >
        {label}
      </button>
    )
  }

  // Search narrows within the chosen category; a category with no match drops out rather than showing an empty heading.
  const q = query.trim().toLowerCase()
  const matches = (s: Starter) => !q || [s.name, s.description, s.category].some(t => t.toLowerCase().includes(q))
  const visible = groups
    .filter(([c]) => !category || c === category)
    .map(([c, items]) => [c, items.filter(matches)] as const)
    .filter(([, items]) => items.length > 0)

  return (
    <div>
      <input
        type="search"
        aria-label="Search layouts"
        placeholder="Search layouts"
        value={query}
        onChange={e => setQuery(e.target.value)}
        style={{ ...fieldStyle, width: 320, maxWidth: '100%', marginBottom: spacing.sm }}
      />
      <div role="group" aria-label="Filter by category" style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.md }}>
        {chip('All', null)}
        {groups.map(([c]) => chip(capitalize(c), c))}
      </div>

      {visible.length === 0 && (
        <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>No layouts match “{query.trim()}”.</div>
      )}

      {visible.map(([cat, items]) => (
        <section key={cat} aria-label={capitalize(cat)} style={{ marginBottom: spacing.lg }}>
          <h2 style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', margin: `0 0 ${spacing.sm}` }}>
            {capitalize(cat)}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))', gap: spacing.md }}>
            {items.map(s => (
              <button
                key={s.key}
                type="button"
                onClick={() => setOpen(s)}
                aria-label={`Preview ${s.name}`}
                style={{
                  display: 'flex', flexDirection: 'column', padding: 0, textAlign: 'left', cursor: 'pointer',
                  background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md,
                  boxShadow: shadow.sm, overflow: 'hidden', minWidth: 0,
                }}
              >
                <StarterThumbnail starterKey={s.key} name={s.name} />
                <div style={{ padding: spacing.sm, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: spacing.xs }}>
                    <span style={{ fontWeight: 600, color: colors.text, fontSize: fontSize.base }}>{s.name}</span>
                    <span style={{
                      fontSize: '11px', padding: '1px 8px', borderRadius: radius.full, whiteSpace: 'nowrap',
                      background: colors.background, color: colors.textSecondary, border: `1px solid ${colors.border}`,
                    }}>{capitalize(s.category)}</span>
                  </div>
                  <span style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>{s.description}</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      ))}

      {open && (
        <StarterPreviewModal
          starter={open}
          readOnly={readOnly}
          initialFolderId={initialFolderId}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  )
}
